/**
 * 聊天服务
 * */
import dayjs from 'dayjs';
import { textToEmojiText } from '@/utils/emoji';
import Msg from './msg-class';
import { im as iMRequest, AIchat } from '@/request/index.js';
import BaseServer from '@/domain/common/base.server';
import useMsgServer from '../common/msg.server';
import useRoomBaseServer from '../room/roombase.server';
import useGroupServer from '../group/StandardGroupServer';
import useMicServer from '../media/mic.server';

import { debounce, throttling, CNTz2CurTz } from '@/utils';

class ChatServer extends BaseServer {
  constructor() {
    if (typeof ChatServer.instance === 'object') {
      return ChatServer.instance;
    }
    super();
    //消息sdk
    this.state = {
      //聊天记录
      chatList: [],
      //私聊列表
      privateChatList: [],
      //预览图片地址
      imgUrls: [],
      //当前用户禁言状态
      banned: false,
      //当前频道全部禁言状态
      allBanned: false,
      //全体禁言状态下各模块生效信息
      allBannedModuleList: {},
      limit: 10,
      curMsg: null,//当前正在编辑的消息
      prevTime: '',//用来记录每条消息的上一条消息发送的时间
      curPrivateTargetId: '',//当前私聊对象id
      isStopAdd: false,//是否停止往消息列表中追加消息，处理停留某条消息场景
      chatMsgRecommend: null//精选消息
    };
    //节流暂存消息池
    this.MSGQUEUE = []
    this.listenEvents();
    this.controller = null;
    ChatServer.instance = this;
    return this;
  }
  init() {
    const { interactToolStatus, beforeInitData } = useRoomBaseServer().state;
    this.state.chatMsgRecommend = beforeInitData.chat_msg_recommend
    const { groupInitData } = useGroupServer().state;
    this.state.banned = groupInitData.isInGroup
      ? groupInitData.is_banned == 1
        ? true
        : false
      : interactToolStatus.is_banned == 1
        ? true
        : false; //1禁言 0取消禁言
    this.state.allBanned = groupInitData.isInGroup
      ? false
      : interactToolStatus.all_banned == 1
        ? true
        : false; //1禁言 0取消禁言
    this.state.allBannedModuleList = {
      chat_status: interactToolStatus.chat_status == 1 ? true : false, //1生效 0不生效
      qa_status: interactToolStatus.qa_status == 1 ? true : false, //1生效 0不生效
      private_chat_status: interactToolStatus.private_chat_status == 1 ? true : false //1生效 0不生效
    };
  }
  Events = {
    //个人禁言
    BANNED: 'banned',
    //全体禁言
    ALLBANNED: 'allBanned'
  };
  //setSate
  setState(key, value) {
    ChatServer.instance.state[key] = value;
  }
  //监听msgServer通知
  listenEvents() {
    const msgServer = useMsgServer();
    const groupServer = useGroupServer();
    //监听聊天消息
    msgServer.$onMsg('CHAT', rawMsg => {

      const { role_name } = useRoomBaseServer().state.watchInitData.join_info;
      if (['text', 'image'].includes(rawMsg.data.type)) {
        // socket过来的消息的时间是中国时间，需要转成当前时区的时间
        rawMsg.date_time = CNTz2CurTz(rawMsg.date_time)
        // ios客戶端发送的聊天消息不含 barrageTxt 字段导致没有弹幕，所以需要兼容
        if (!rawMsg.data.barrageTxt) {
          rawMsg.data.barrageTxt = rawMsg.data.text_content
        }
        //表情处理
        rawMsg.data.text_content = textToEmojiText(rawMsg.data.text_content);
        //格式化消息用于渲染并添加到消息列表
        //私聊我的消息，//自己发的消息不处理
        if (rawMsg.data.target_id) {
          if (this.isMyMsg(rawMsg)) {
            if (role_name == 2) {
              this.setCurPrivateTarget(rawMsg.sender_id);
            }
            if (role_name == 2 || this.state.curPrivateTargetId == rawMsg.sender_id) {
              this.state.privateChatList.push(Msg._handleGenerateMsg(rawMsg));
            }

            this.$emit('receivePrivateMsg', Msg._handleGenerateMsg(rawMsg));
          }
          return;
        }
        const msg = Msg._handleGenerateMsg(rawMsg);
        msg.prevTime = this.state.prevTime;

        //自己发的消息不处理(来自聊天审核页的除外)
        if (!this.isSelfMsg(rawMsg) || rawMsg?.data?.access_audit == 1 || useRoomBaseServer().state.configList?.message_review_before_send == 1) {
          if (!this.state.isStopAdd) {
            this.MSGQUEUE.push(msg)
            this.throttleAddMsg()
          }
        }
        this.state.prevTime = msg.sendTime;
        //非自己发送的普通消息
        if (!this.isSelfMsg(rawMsg)) {
          this.$emit('receiveMsg', rawMsg);
        }
        //@当前用户
        if (this.isAtMe(rawMsg)) {
          this.$emit('atMe', rawMsg);
        }
        //回复当前用户
        if (this.isReplyMe(rawMsg)) {
          this.$emit('replyMe', rawMsg);
        }
      }
      // 禁言当前用户
      if (rawMsg.data.type === 'disable' && this.isMyMsg(rawMsg)) {
        this.setLocalBanned(true);
        useMicServer().speakOff({ sourceInfo: 'chat.server,禁言当前用户' });
        this.$emit('banned', this.state.banned);
      }
      //取消禁言当前用户
      if (rawMsg.data.type === 'permit' && this.isMyMsg(rawMsg)) {
        this.setLocalBanned(false);
        this.$emit('banned', this.state.banned);
      }
      // 开启全体禁言
      if (rawMsg.data.type === 'disable_all') {
        this.setLocalAllBanned(true);
        this.setAllBannedModuleList(rawMsg.data);
        if (role_name == 2) {
          useMicServer().speakOff({ sourceInfo: 'chat.server,开启全体禁言' });
        }
        this.$emit('allBanned', this.state.allBanned, rawMsg.data);
      }
      // 关闭全体禁言
      if (rawMsg.data.type === 'permit_all') {
        this.setLocalAllBanned(false);
        this.setAllBannedModuleList(rawMsg.data);
        this.$emit('allBanned', this.state.allBanned, rawMsg.data);
      }

    });
    msgServer.$onMsg('CUSTOM_MSG', rawMsg => {
      //清空聊天
      if (rawMsg.data.type === 'clear_chat_message') {
        this.state.chatList.splice(0, this.state.chatList.length)
      }
    })
    msgServer.$onMsg('ROOM_MSG', rawMsg => {

      //删除某条聊天消息
      if (rawMsg.data.type === 'chat_delete') {
        const ids = rawMsg.data.msg_ids.split(',')
        for (let i = this.state.chatList.length - 1; i >= 0; i--) {
          if (ids.includes(this.state.chatList[i].msgId)) {
            this.state.chatList.splice(i, 1);
          }
        }
      }

      if (rawMsg.data.type === 'room_kickout' && this.isMyMsg(rawMsg)) {
        this.$emit('roomKickout');
      }
      if (rawMsg.data.type == 'live_over') {
        this.state.allBanned = false;
        this.setAllBannedModuleList({
          chat_status: 1,
          qa_status: 1,
          private_chat_status: 1
        });
      }
      if (rawMsg.data.type == 'chat_msg_set_recommend') {
        this.state.chatMsgRecommend = {
          msg_id: rawMsg.data.msg_id,
          msg_content: rawMsg.data.msg_content,
        }
        this.$emit("recommendChatMsg", this.state.chatMsgRecommend)

      } else if (rawMsg.data.type == 'chat_msg_del_recommend') {
        this.state.chatMsgRecommend = null;
        this.$emit("cancelRecommendChatMsg", this.state.chatMsgRecommend)

      } else if (rawMsg.data.type == 'chat_review_config') {
        const data = JSON.parse(rawMsg.data.content || "{}")
        this.$emit("chatReviewConfig", data)
      }
    });
    //接收频道变更通知
    msgServer.$on(msgServer.EVENT_TYPE.CHANNEL_CHANGE, () => {
      this.$emit('changeChannel');
    });
    //监听进出子房间消息
    groupServer.$on('ROOM_CHANNEL_CHANGE', () => {
      const { groupInitData } = groupServer.state;
      const { interactToolStatus } = useRoomBaseServer().state;
      if (groupInitData.isInGroup) {
        this.setLocalAllBanned(false);
        this.setLocalBanned(groupInitData.is_banned == 1 ? true : false);
        this.$emit('banned', this.state.banned);
        this.$emit('allBanned', false);
      } else {
        this.setLocalAllBanned(interactToolStatus.all_banned == 1 ? true : false);
        this.setLocalBanned(interactToolStatus.is_banned == 1 ? true : false);
        this.$emit('allBanned', interactToolStatus.all_banned == 1 ? true : false);
        this.$emit('banned', interactToolStatus.is_banned == 1 ? true : false);
      }
    });
  }
  throttleAddMsg = throttling(() => {
    if (!this.state.isStopAdd) {
      this.state.chatList.push(...this.MSGQUEUE)
      if (this.state.chatList.length > 50) {
        this.state.chatList.splice(0, this.state.chatList.length - 50)
      }
    }
    this.MSGQUEUE.length = 0
  }, 1000)
  setCurPrivateTarget(targetId) {
    this.state.curPrivateTargetId = targetId;
  }
  // 判断是不是自己发的消息
  isSelfMsg(msg) {
    const { watchInitData } = useRoomBaseServer().state;
    return msg.sender_id == watchInitData.join_info.third_party_user_id;
  }
  //判断是不是发送给当前用户的消息
  isMyMsg(msg) {
    const { watchInitData } = useRoomBaseServer().state;
    console.log(msg.data.target_id, '-', watchInitData.join_info);
    return msg.data.target_id == watchInitData.join_info.third_party_user_id;
  }
  //判断是不是@当前用户
  isAtMe(msg) {
    if (msg.context.atList && msg.context.atList.length && msg.data.text_content) {
      return msg.context.atList.some(a => {
        return a.accountId == useRoomBaseServer().state.watchInitData.join_info.third_party_user_id;
      });
    }
    return false;
  }
  //判断是不是回复当前用户
  isReplyMe(msg) {
    if (msg.context.replyMsg) {
      return (
        msg.context.replyMsg.sendId ==
        useRoomBaseServer().state.watchInitData.join_info.third_party_user_id
      );
    }
    return false;
  }
  //接收聊天消息
  async getHistoryMsg(params, isReset) {
    if (isReset) {
      delete params.msg_id;
      delete params.anchor_path;
    }
    //请求获取聊天消息
    let historyList = await this.fetchHistoryData(params);
    let list = ((params.isfirst ? historyList.data : historyList.data.list) || []).map(item => {
      if (params.isfirst) {
        item.data = {
          text_content: item.data,
          type: item.type,
          barrage_txt: item.data,
          image_urls: item.image_urls,
          nickname: item.nick_name,
          avatar: item.avatar,
        };
        item.sender_id = item.third_party_user_id;
      }

      if (item.data?.type !== "system_notice") {
        //处理普通内容
        item.data.text_content &&
          (item.data.text_content = textToEmojiText(item.data.text_content));
        //处理图片预览
        item.data.image_urls && ChatServer._handleImgUrl.call(this, item.data.image_urls);
      }

      //处理私聊列表
      if (
        item.context &&
        Array.isArray(item.context.at_list) &&
        item.context.at_list.length &&
        item.data.text_content
      ) {
        item.context.at_list = ChatServer._handlePrivateChatList(item, item.context.at_list);
        //发起端的特殊处理，可以考虑统一
        item.context.atList = item.context.at_list;
      }
      //实例化消息
      const msg = Msg._handleGenerateMsg(item, true);
      msg.prevTime = this.state.prevTime;
      this.state.prevTime = msg.sendTime;
      return msg; //第二个参数区别是否为历史消息
    }).reverse().reduce((acc, curr) => {
      const showTime = curr.showTime;
      acc.some(s => s.showTime == showTime)
        ? acc.push({ ...curr, showTime: '' })
        : acc.push(curr);
      return acc;
    }, []).filter(item => !['customPraise'].includes(item.type));
    isReset ? this.state.chatList = list : this.state.chatList.unshift(...list);
    console.log('chatList', this.state.chatList);

    //返回原始数据等以方便使用
    return {
      historyList,
      list,
      chatList: this.state.chatList,
      imgUrls: this.state.imgUrls || []
    };
  }
  //往聊天列表里手动添加消息
  addChatToList(item) {
    item.count = Msg.getcount();
    if (!this.state.isStopAdd) {
      this.MSGQUEUE.push(item)
    }
    this.$emit('receiveMsg', item);
    this.throttleAddMsg()

  }
  // 清空普通聊天消息
  clearChatMsg() {
    this.state.chatList.splice(0, this.state.chatList.length);
  }
  //清空私聊列表
  clearPrivateChatMsg() {
    this.state.privateChatList.splice(0, this.state.privateChatList.length);
  }
  //创建当前编辑消息
  createCurMsg() {
    this.curMsg = this.curMsg || new Msg();
    return this.curMsg;
  }
  //销毁当前编辑消息
  clearCurMsg() {
    this.curMsg = null;
  }
  //防抖处理发送聊天消息
  sendMsg = debounce(this.sendChatMsg.bind(this), 300, true);
  //发送聊天消息
  sendChatMsg({ data, context }) {
    const message_review_before_send = useRoomBaseServer().state.configList?.message_review_before_send
    if (data.target_id) {
      data.access_audit = 0; // 私聊补充字段
    }
    useMsgServer().sendChatMsg(data, context);
    const msg = Msg._handleGenerateMsg({ data, context });
    const date_time = dayjs().format('YYYY-MM-DD HH:mm:ss');
    msg.sendTime = date_time;
    if (data.target_id) {
      this.state.privateChatList.push(msg);
    } else {
      if (message_review_before_send != 1) {
        msg.prevTime = this.state.chatList[this.state.chatList.length - 1]?.sendTime;
        this.state.chatList.push(msg);
      }
    }
    data.text_content = textToEmojiText(data.text_content);
  }

  //发起请求，或者聊天记录数据
  /**
   * 获取历史数据的方法
   * @param {Object} params - 请求参数对象
   * @returns {Promise} 返回一个Promise对象，包含聊天列表数据
   */
  fetchHistoryData(params) {
    // 检查参数是否存在且为对象类型
    if (params && typeof params === 'object') {
      // 设置消息类型为文本、图片和系统通知
      params.msg_type = 'text,image,system_notice';
      // 如果是第一次请求
      if (params.isfirst) {
        // 从房间基础服务中获取交互和加入信息
        const { interact, join_info } = useRoomBaseServer().state?.watchInitData;
        params.app_id = interact?.paas_app_id;
        params.channel_id = interact?.channel_id;
        // 设置频道ID
        params.package_check = '1.7.1';
        // 设置版本检查
        params.access_token = interact?.paas_access_token;
        // 设置访问令牌
        params.third_party_user_id = join_info?.third_party_user_id
        // 设置第三方用户ID
      }
    }
    return iMRequest.chat.getChatList(params);
    // 发送聊天列表请求并返回结果
  }

  //检测是否包含敏感词
  checkHasKeyword(inputValue) {
    const keywordList = useRoomBaseServer().state.keywords.list;
    console.log('keywordList', keywordList);
    let filterStatus = true;
    if (keywordList && keywordList.length) {
      //只要找到一个敏感词，消息就不让发
      filterStatus = !keywordList.some(item => inputValue.includes(item.name));
    }

    return filterStatus;
  }

  //私有方法，处理图片链接
  static _handleImgUrl(rawData) {
    this.state.imgUrls.push(...rawData);
  }

  //私有方法，处理私聊列表(待梳理)
  static _handlePrivateChatList(item, list = [], from = '观看端') {
    if (['观看端'].includes(from)) {
      return list.map(a => {
        item.data.text_content = item.data.text_content.replace(
          `***${a.nick_name}`,
          `@${a.nick_name}`
        );
        return a;
      });
    }

    if (['h5'].includes(from)) {
      return list.map(a => {
        // 向前兼容fix 14968  历史消息有得是@
        if (item.data.text_content.indexOf('***') >= 0) {
          item.data.text_content = item.data.text_content.replace(
            `***${a.nick_name}`,
            `<span style='color:#4da1ff;float:left'>@${a.nick_name} &nbsp;</span> `
          );
        } else {
          item.data.text_content = item.data.text_content.replace(
            `@${a.nick_name}`,
            `<span style='color:#4da1ff;float:left'>@${a.nick_name} &nbsp;</span> `
          );
        }
        return a;
      });
    }
  }
  //设置本地个人禁言状态
  setLocalBanned(flag) {
    const groupInitData = useGroupServer().state.groupInitData;
    if (groupInitData.isInGroup) {
      groupInitData.is_banned = flag;
    }
    this.state.banned = flag;
  }
  //设置本地全员禁言状态
  setLocalAllBanned(flag) {
    this.state.allBanned = flag;
  }
  //设置全体禁言状态下各模块生效状态
  setAllBannedModuleList(data) {
    const { chat_status, qa_status, private_chat_status } = data;
    this.state.allBannedModuleList.chat_status = chat_status == 1 ? true : false;
    this.state.allBannedModuleList.qa_status = qa_status == 1 ? true : false;
    this.state.allBannedModuleList.private_chat_status = private_chat_status == 1 ? true : false;
  }
  /**
   * 禁言/取消禁言
   * */
  setBanned(params = {}) {
    return iMRequest.chat.setBanned(params).then(res => {
      if (res.code == 200) {
        this.state.banned = params.status == 1 ? true : false;
      }
    });
  }

  /**
   * 全体禁言
   * /v3/interacts/chat-user/set-all-banned
   * */
  setAllBanned(params = {}) {
    return iMRequest.chat.setAllBanned(params).then(res => {
      if (res.code == 200) {
        this.state.allBanned = params.status == 1 ? true : false;
      }
      return res
    });
  }

  /**
   * 删除消息
   * /v3/interacts/chat/delete-message
   * */
  deleteMessage(params) {
    const _index = this.state.chatList.findIndex(chatMsg => {
      return chatMsg.msgId === params.msg_id;
    });
    _index > -1 && this.state.chatList.splice(_index, 1);
    return iMRequest.chat.deleteMessage(params);
  }

  /**
   * 踢出
   * /v3/interacts/chat-user/set-kicked
   * */
  setKicked(params = {}) {
    return iMRequest.chat.setKicked(params);
  }

  /**
   * 获取关键词
   * */
  getKeyWordsList(params = {}) {
    return iMRequest.keyWords.getKeyWordsList(params);
  }
  //获取禁言用户列表
  getBannedList(params = {}) {
    return iMRequest.chat.getBannedList(params);
  }
  //获取提出用户列表
  getKickedList(params = {}) {
    return iMRequest.chat.getKickedList(params);
  }
  //请求全局踢出列表
  getGlobalKickedList(params = {}) {
    return iMRequest.chat.getGlobalKickedList(params);
  }
  //获取私聊的联系人列表
  getPrivateContactList(params = {}) {
    return iMRequest.chat.fetchPrivateContactList(params);
  }
  //将联系人加入到私聊列表
  addToRankList(params = {}) {
    return iMRequest.privateChat.setRankList(params);
  }
  //获取私聊的记录列表
  getPrivateChatHistoryList(params = {}) {
    const { watchInitData } = useRoomBaseServer().state;
    params.room_id = watchInitData.interact.room_id;
    params.webinar_id = watchInitData.webinar.id;
    return iMRequest.chat.fetchPrivateChatHistoryList(params).then(res => {
      if (res.code == 200) {
        let list = res.data.list
          .map(item => {
            item.data.text_content &&
              (item.data.text_content = textToEmojiText(item.data.text_content));
            //处理图片预览
            item.data.image_urls && ChatServer._handleImgUrl.call(this, item.data.image_urls);
            return Msg._handleGenerateMsg(item);
          })
          .reduce((acc, curr) => {
            const showTime = curr.showTime;
            acc.some(s => s.showTime === showTime)
              ? acc.push({ ...curr, showTime: '' })
              : acc.push(curr);
            return acc;
          }, [])
          .reverse();
        this.state.privateChatList.splice(0, 0, ...list);
      }
    });
  }
  //设置当前server属性
  setState(key, value) {
    this.state[key] = value;
  }
  // 设置聊天昵称是否加密
  setNicknameEncryption(status) {
    const params = {
      status
    }
    const { watchInitData } = useRoomBaseServer().state;
    params.room_id = watchInitData.interact.room_id;
    params.webinar_id = watchInitData.webinar.id;
    return iMRequest.chat.setNicknameEncryption(params);
  }

  // 设置观众禁言模式 0-用户有感知 1-用户无感知
  setBannedMode(mode) {
    const params = {
      mode
    }
    const { watchInitData } = useRoomBaseServer().state;
    params.room_id = watchInitData.interact.room_id;
    params.webinar_id = watchInitData.webinar.id;
    return iMRequest.chat.setBannedMode(params);
  }

  getTopicList(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;

    return AIchat.getTopicList(params)
  }
  copyTopic(params) {
    return AIchat.copyTopic(params)
  }
  saveTopic(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;
    return AIchat.saveTopic(params)
  }
  deleteTopic(params) {
    return AIchat.deleteTopic(params)
  }
  checkImportBot(params) {
    return AIchat.checkImportBot(params)
  }
  getTopicDetail(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;

    return AIchat.getTopicDetail(params)
  }
  getBotRandom(params) {
    return AIchat.getBotRandom(params)
  }
  //推送
  startPushTopic(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;

    return AIchat.startPushTopic(params)
  }

  //停止
  stopPushTopic(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;

    return AIchat.stopPushTopic(params)
  }

  //虚拟观众列表
  getVirtualList(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;

    return AIchat.getVirtualList(params)
  }

  //推送机器人消息
  pushMessage(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;

    return AIchat.pushMessage(params)
  }

  //机器人列表
  getBotList(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;

    return AIchat.getBotList(params)
  }
  //获取常用语列表
  getPhraseList(params) {
    const { watchInitData } = useRoomBaseServer().state;
    // params.webinar_id = watchInitData.webinar.id;
    return AIchat.getPhraseList(params)
  }
  //观众端获取聊天快捷语
  getPhraseSimpleList() {
    const { watchInitData } = useRoomBaseServer().state;
    const params = {
      webinar_id: watchInitData.webinar.id,
      chat_scene: 99,
      chat_usage: 1,
      order: 'asc',
      status: 1
    }
    return AIchat.getPhraseSimpleList(params)
  }
  //保存常用语
  savePhrase(params) {
    const { watchInitData } = useRoomBaseServer().state;
    // params.webinar_id = watchInitData.webinar.id;
    return AIchat.savePhrase(params)
  }
  //删除常用语
  deletePhrase(params) {
    const { watchInitData } = useRoomBaseServer().state;
    // params.webinar_id = watchInitData.webinar.id;
    return AIchat.deletePhrase(params)
  }
  //推送机器人消息
  pushChatbotMessage(params) {
    const { watchInitData } = useRoomBaseServer().state;
    params.webinar_id = watchInitData.webinar.id;
    return AIchat.pushChatbotMessage(params)
  }
  //设置精选消息
  async handleRecommendMsg(params) {
    const res = await iMRequest.chat.recommendMsg(params);
    if (res.code == 200) {
      if (params.recommend_status == 1) {
        this.state.chatMsgRecommend = { msg_id: params.msg_id, msg_content: params.msg_content }
      } else {
        this.state.chatMsgRecommend = null
      }
    }
    return res
  }
}

// 导出一个默认函数，用于创建ChatServer实例
export default function useChatServer() {
  // 创建ChatServer实例
  return new ChatServer();
}
